/**
 * TrustGrid Control Detail Page
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Tabs, Card, Tag, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { getControl, getControlProjects } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';
import { handleCrossOrgError } from '../utils/errorHandlers';
import TrustGridPage from '../../../components/layout/TrustGridPage';
import { TGDataTable, LoadingState } from '../../../components/common';
import { colors, spacing } from '../../../theme/designTokens';
import ControlConfigPanel from './ControlConfigPanel';
import PIIDataQualityDashboard from './PIIDataQualityDashboard';
import CTRL_02_ConfigPanel from './CTRL_02_ConfigPanel';
import CTRL_02_Dashboard from './CTRL_02_Dashboard';
import CTRL_03_ConfigPanel from './CTRL_03_ConfigPanel';
import CTRL_03_Dashboard from './CTRL_03_Dashboard';
import CTRL_04_ConfigPanel from './CTRL_04_ConfigPanel';
import CTRL_04_Dashboard from './CTRL_04_Dashboard';
import CTRL_05_ConfigPanel from './CTRL_05_ConfigPanel';
import CTRL_05_Dashboard from './CTRL_05_Dashboard';
import CTRL_06_ConfigPanel from './CTRL_06_ConfigPanel';
import CTRL_06_Dashboard from './CTRL_06_Dashboard';
import CTRL_07_ConfigPanel from './CTRL_07_ConfigPanel';
import CTRL_07_FairnessDashboard from './CTRL_07_FairnessDashboard';
import CTRL_08_ConfigPanel from './CTRL_08_ConfigPanel';
import CTRL_08_Dashboard from './CTRL_08_Dashboard';
import CTRL_09_ConfigPanel from './CTRL_09_ConfigPanel';
import CTRL_09_Dashboard from './CTRL_09_Dashboard';
import CTRL_10_ConfigPanel from './CTRL_10_ConfigPanel';
import CTRL_10_Dashboard from './CTRL_10_Dashboard';
import GenericControlConfigPanel from './GenericControlConfigPanel';
import ControlConfigFactory from './ControlConfigFactory';
import RegulationDisplay from './RegulationDisplay';
import GenericTestModal from './GenericTestModal';
import ImplementationGuide from './ImplementationGuide';
import LastScanFilterBar from './LastScanFilterBar';
import { useTrustGrid } from '../../../contexts/TrustGridContext';

function ControlDetail() {
  const { controlCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProjectId, selectProject } = useTrustGrid();
  const [control, setControl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);

  // Build breadcrumbs
  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = location.state?.breadcrumbs || [
      { label: 'TrustGrid', path: '/trustgrid/dashboard' },
    ];
    return [
      ...baseBreadcrumbs,
      { label: 'Controls', path: '/trustgrid/controls' },
      { label: controlCode, path: `/trustgrid/controls/${controlCode}` }
    ];
  }, [location.state, controlCode]);

  useEffect(() => {
    fetchControl();
    fetchProjects();
  }, [controlCode]);

  const fetchControl = async () => {
    setLoading(true);
    try {
      const res = await getControl(controlCode);
      setControl(res.data);
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'ControlDetail');
      if (!wasHandled) {
        console.error('Failed to fetch control:', error);
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      const res = await getControlProjects(controlCode);
      setProjects(res.data.projects || []);
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'ControlDetail');
      if (!wasHandled) {
        console.error('Failed to fetch projects:', error);
        showError(error);
      }
    } finally {
      setLoadingProjects(false);
    }
  };

  // Generate dynamic status filter options
  const statusOptions = useMemo(() => {
    const statuses = [...new Set(projects.map(p => p.status))].filter(Boolean).sort();
    return statuses.map(s => ({ label: s.charAt(0).toUpperCase() + s.slice(1), value: s }));
  }, [projects]);

  // Filter projects by date
  const filteredProjects = useMemo(() => {
    if (!dateFilter || !dateFilter.date) return projects;

    const { operator, date } = dateFilter;
    const filterTime = new Date(date).setHours(0, 0, 0, 0);

    return projects.filter((project) => {
      if (!project.created_at) return false;
      const projectTime = new Date(project.created_at).setHours(0, 0, 0, 0);

      switch (operator) {
        case 'on':
          return projectTime === filterTime;
        case 'after':
          return projectTime > filterTime;
        case 'before':
          return projectTime < filterTime;
        default:
          return true;
      }
    });
  }, [projects, dateFilter]);

  if (loading || !control) return <LoadingState type="spinner" />;

  const projectColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => <Tag color={colors.primary[500]}>{status}</Tag>,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: statusOptions,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString(),
      sortable: true,
    }
  ];

  // Build tabs based on control type
  const buildTabItems = () => {
    const baseItems = [
      {
        key: '1',
        label: 'Definition',
        children: (
          <Card>
            <div style={{ padding: spacing.lg }}>
              <h3 style={{ color: colors.text.primary }}>Description</h3>
              <p style={{ color: colors.text.secondary }}>{control.description}</p>

              <h3 style={{ marginTop: spacing.lg, color: colors.text.primary }}>Severity</h3>
              <Tag
                color={
                  control.severity === 'critical' ? colors.severity.critical.main :
                  control.severity === 'high' ? colors.severity.high.main :
                  control.severity === 'medium' ? colors.severity.medium.main :
                  colors.severity.low.main
                }
                style={{ fontSize: '14px', padding: `${spacing.xs} ${spacing.sm}` }}
              >
                {control.severity}
              </Tag>

              <h3 style={{ marginTop: spacing.lg, color: colors.text.primary }}>Applicable Regulations</h3>
              <RegulationDisplay
                regulationData={control.regulation_data}
                legacyRegulation={control.regulation}
              />
            </div>
          </Card>
        )
      },
      {
        key: '2',
        label: 'Detection Logic',
        children: (
          <Card>
            <div style={{ padding: spacing.lg }}>
              <h3 style={{ color: colors.text.primary }}>Detection Logic</h3>
              <p style={{ whiteSpace: 'pre-wrap', color: colors.text.secondary }}>{control.detection_logic}</p>
            </div>
          </Card>
        )
      },
      {
        key: '3',
        label: 'Remediation',
        children: (
          <Card>
            <div style={{ padding: spacing.lg }}>
              <h3 style={{ color: colors.text.primary }}>Remediation Guidance</h3>
              <p style={{ whiteSpace: 'pre-wrap', marginBottom: spacing.md, color: colors.text.secondary }}>{control.remediation_guidance}</p>
              <Button type="primary" onClick={() => console.log('Create case')}>
                Create Case
              </Button>
            </div>
          </Card>
        )
      },
      {
        key: 'implementation',
        label: '📘 Implementation Guide',
        children: (
          <ImplementationGuide
            controlCode={control.code}
            controlTitle={control.title}
          />
        )
      }
    ];

    // Add Configuration tab for CTRL_01 when a project is selected
    if (control.code === 'CTRL_01' && selectedProjectId) {
      baseItems.push({
        key: 'config',
        label: '⚙️ Configuration',
        children: (
          <ControlConfigPanel
            projectId={selectedProjectId}
            controlCode={control.code}
            project={projects.find(p => p.id === selectedProjectId)}
          />
        )
      });

      // Add Data Quality Dashboard tab for CTRL_01
      baseItems.push({
        key: 'data-quality',
        label: '📊 Data Quality',
        children: (
          <PIIDataQualityDashboard
            projectId={selectedProjectId}
          />
        )
      });
    }

    // Add Configuration tab for CTRL_07 when a project is selected
    if (control.code === 'CTRL_07' && selectedProjectId) {
      baseItems.push({
        key: 'config',
        label: '⚙️ Configuration',
        children: (
          <CTRL_07_ConfigPanel
            projectId={selectedProjectId}
            controlCode={control.code}
            project={projects.find(p => p.id === selectedProjectId)}
          />
        )
      });

      // Add Fairness Dashboard tab for CTRL_07
      baseItems.push({
        key: 'fairness-metrics',
        label: '📊 Fairness Metrics',
        children: (
          <CTRL_07_FairnessDashboard
            projectId={selectedProjectId}
          />
        )
      });
    }

    // Add Configuration tab for CTRL_08 when a project is selected
    if (control.code === 'CTRL_08' && selectedProjectId) {
      baseItems.push({
        key: 'config',
        label: '⚙️ Configuration',
        children: (
          <CTRL_08_ConfigPanel
            projectId={selectedProjectId}
            controlCode={control.code}
            project={projects.find(p => p.id === selectedProjectId)}
          />
        )
      });

      // Add Human Oversight Dashboard tab for CTRL_08
      baseItems.push({
        key: 'dashboard',
        label: '📊 Oversight Dashboard',
        children: (
          <CTRL_08_Dashboard
            projectId={selectedProjectId}
          />
        )
      });
    }

    // Add Configuration tab for CTRL_09 when a project is selected
    if (control.code === 'CTRL_09' && selectedProjectId) {
      baseItems.push({
        key: 'config',
        label: '⚙️ Configuration',
        children: (
          <CTRL_09_ConfigPanel
            projectId={selectedProjectId}
            controlCode={control.code}
            project={projects.find(p => p.id === selectedProjectId)}
          />
        )
      });

      // Add Data Residency Dashboard tab for CTRL_09
      baseItems.push({
        key: 'dashboard',
        label: '📊 Residency Dashboard',
        children: (
          <CTRL_09_Dashboard
            projectId={selectedProjectId}
          />
        )
      });
    }

    // Add Configuration tab for CTRL_02 when a project is selected
    if (control.code === 'CTRL_02' && selectedProjectId) {
      baseItems.push({
        key: 'config',
        label: '⚙️ Configuration',
        children: (
          <CTRL_02_ConfigPanel
            projectId={selectedProjectId}
            controlCode={control.code}
            project={projects.find(p => p.id === selectedProjectId)}
          />
        )
      });

      // Add Risk Classification Dashboard tab for CTRL_02
      baseItems.push({
        key: 'dashboard',
        label: '📊 Risk Dashboard',
        children: (
          <CTRL_02_Dashboard
            projectId={selectedProjectId}
          />
        )
      });
    }

    // Add Configuration tab for CTRL_06 when a project is selected
    if (control.code === 'CTRL_06' && selectedProjectId) {
      baseItems.push({
        key: 'config',
        label: '⚙️ Configuration',
        children: (
          <CTRL_06_ConfigPanel
            projectId={selectedProjectId}
            controlCode={control.code}
            project={projects.find(p => p.id === selectedProjectId)}
          />
        )
      });

      // Add Performance Monitoring Dashboard tab for CTRL_06
      baseItems.push({
        key: 'dashboard',
        label: '📊 Performance Dashboard',
        children: (
          <CTRL_06_Dashboard
            projectId={selectedProjectId}
          />
        )
      });
    }

    // Add Configuration and Dashboard tabs for CTRL_03 when a project is selected
    if (control.code === 'CTRL_03' && selectedProjectId) {
      baseItems.push({
        key: 'config',
        label: '⚙️ Configuration',
        children: (
          <CTRL_03_ConfigPanel
            controlCode={control.code}
            projectId={selectedProjectId}
            project={projects.find(p => p.id === selectedProjectId)}
          />
        )
      });

      // Add Purpose Limitation Dashboard tab for CTRL_03
      baseItems.push({
        key: 'dashboard',
        label: '📊 Purpose Dashboard',
        children: (
          <CTRL_03_Dashboard
            projectId={selectedProjectId}
          />
        )
      });
    }

    // Add Configuration and Dashboard tabs for CTRL_04 when a project is selected
    if (control.code === 'CTRL_04' && selectedProjectId) {
      baseItems.push({
        key: 'config',
        label: '⚙️ Configuration',
        children: (
          <CTRL_04_ConfigPanel
            controlCode={control.code}
            projectId={selectedProjectId}
            project={projects.find(p => p.id === selectedProjectId)}
          />
        )
      });

      // Add Data Provenance Dashboard tab for CTRL_04
      baseItems.push({
        key: 'dashboard',
        label: '📊 Provenance Dashboard',
        children: (
          <CTRL_04_Dashboard
            projectId={selectedProjectId}
          />
        )
      });
    }

    // Add Configuration and Dashboard tabs for CTRL_05 when a project is selected
    if (control.code === 'CTRL_05' && selectedProjectId) {
      baseItems.push({
        key: 'config',
        label: '⚙️ Configuration',
        children: (
          <CTRL_05_ConfigPanel
            controlCode={control.code}
            projectId={selectedProjectId}
            project={projects.find(p => p.id === selectedProjectId)}
          />
        )
      });

      // Add Data Minimization Dashboard tab for CTRL_05
      baseItems.push({
        key: 'dashboard',
        label: '📊 Minimization Dashboard',
        children: (
          <CTRL_05_Dashboard
            projectId={selectedProjectId}
          />
        )
      });
    }

    // Add Configuration and Dashboard tabs for CTRL_10 when a project is selected
    if (control.code === 'CTRL_10' && selectedProjectId) {
      baseItems.push({
        key: 'config',
        label: '⚙️ Configuration',
        children: (
          <CTRL_10_ConfigPanel
            controlCode={control.code}
            projectId={selectedProjectId}
            project={projects.find(p => p.id === selectedProjectId)}
          />
        )
      });

      // Add Change Management Dashboard tab for CTRL_10
      baseItems.push({
        key: 'dashboard',
        label: '📊 Change Dashboard',
        children: (
          <CTRL_10_Dashboard
            projectId={selectedProjectId}
          />
        )
      });
    }

    // Add Test & Preview tab
    baseItems.push({
      key: 'test',
      label: '🧪 Test & Preview',
      children: (
        <Card>
          <div style={{ padding: spacing.lg, textAlign: 'center' }}>
            <h3 style={{ color: colors.text.primary }}>Test Control Logic</h3>
            <p style={{ color: colors.text.secondary, marginBottom: spacing.lg }}>
              Test {control.code} with sample data and preview expected event structures
            </p>
            <Button
              type="primary"
              size="large"
              onClick={() => setTestModalVisible(true)}
            >
              Open Test & Preview Tool
            </Button>
          </div>
        </Card>
      )
    });

    // Add Projects tab
    baseItems.push({
      key: '4',
      label: `Projects (${projects.length})`,
      children: (
        <Card>
          <div style={{ padding: spacing.lg }}>
            <h3 style={{ marginBottom: spacing.md, color: colors.text.primary }}>Projects Using This Control</h3>
            {loadingProjects ? (
              <LoadingState type="spinner" />
            ) : projects.length === 0 ? (
              <p style={{ color: colors.text.secondary, textAlign: 'center', padding: spacing.xl }}>
                No projects are currently using this control
              </p>
            ) : (
              <TGDataTable
                data={filteredProjects}
                columns={projectColumns}
                rowKey="id"
                loading={false}
                pagination="client"
                pageSize={10}
                showExport
                showColumnConfig
                showFilters
                exportFilename={`${control.code}-projects`}
                toolbar={
                  <LastScanFilterBar
                    dateFilter={dateFilter}
                    onDateFilterChange={setDateFilter}
                  />
                }
                externalFilterCount={dateFilter ? 1 : 0}
                onClearExternalFilters={() => setDateFilter(null)}
              />
            )}
          </div>
        </Card>
      )
    });

    return baseItems;
  };

  const items = buildTabItems();

  return (
    <TrustGridPage
      title={control.title}
      subtitle={control.regulation}
      breadcrumbs={breadcrumbs}
      backButton={{
        onClick: () => navigate('/trustgrid/controls'),
        label: 'Back to Controls'
      }}
    >
      <div style={{ marginBottom: spacing.md }}>
        <Tag style={{ marginBottom: spacing.xs }}>{control.code}</Tag>
      </div>

      <Tabs defaultActiveKey="1" items={items} />

      {/* Test & Preview Modal */}
      {control && (
        <GenericTestModal
          visible={testModalVisible}
          onClose={() => setTestModalVisible(false)}
          controlCode={control.code}
          controlTitle={control.title}
          projectId={selectedProjectId}
        />
      )}
    </TrustGridPage>
  );
}

export default ControlDetail;
